import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  buildPdfTable,
  computeColumnWidths,
  normalizeOrientation,
  pdfFontSize,
  toCdnUrl,
} from "./gridPdfTable";

const pad = (n) => String(n).padStart(2, "0");
const MARGIN = 20;
const CELL_PADDING = 2;
const IMAGE_SIZE = 28; // pt, lado da foto na célula
const IMAGE_PX = 96; // resolução da miniatura embutida (mantém o PDF leve)
const IMAGE_CONCURRENCY = 16;
const IMAGE_TIMEOUT_MS = 8000;
const IMAGE_BUDGET_MS = 30000; // teto total: o que não carregou até aqui sai sem foto

// Miniaturas já geradas nesta sessão (só sucesso): gerar de novo (outra orientação, outro
// relatório com os mesmos produtos) não baixa nada.
const thumbCache = new Map();

// Monta o PDF com todas as linhas recebidas e só as colunas recebidas
// (quem chama já passa apenas as colunas visíveis na tela). null = nada a imprimir.
// imageData: { [url]: dataURL } já carregado; resolveImageUrl: caminho → URL.
export const buildGridPdf = ({
  columns,
  rows,
  orientation = "portrait",
  title = "",
  imageData = {},
  resolveImageUrl = (p) => p,
}) => {
  const orient = normalizeOrientation(orientation);
  const { head, body, images, columns: cols } = buildPdfTable(columns, rows);
  if (!cols.length) return null;

  const doc = new jsPDF({ orientation: orient, unit: "pt", format: "a4" });
  const fontSize = pdfFontSize(cols.length, orient);
  const now = new Date();
  const stamp = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(
    now.getHours(),
  )}:${pad(now.getMinutes())}`;

  doc.setFontSize(12);
  doc.text(title || "Relatório", 30, 30);
  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.text(`${body.length} registro(s) - ${stamp}`, 30, 44);

  doc.setFontSize(fontSize);
  doc.setFont("helvetica", "bold"); // cabeçalho é negrito: mede pelo mais largo
  const available = doc.internal.pageSize.getWidth() - MARGIN * 2;
  const { widths, fits } = computeColumnWidths({
    head,
    body,
    columns: cols,
    measure: (t) => doc.getTextWidth(t),
    available,
    padding: CELL_PADDING * 2 + 2,
    imageWidth: IMAGE_SIZE + CELL_PADDING * 2,
  });
  doc.setFont("helvetica", "normal");

  const columnStyles = {};
  widths.forEach((w, i) => {
    columnStyles[i] = cols[i].isImage
      ? { cellWidth: w, minCellHeight: IMAGE_SIZE + CELL_PADDING * 2 }
      : { cellWidth: w };
  });

  autoTable(doc, {
    head,
    body,
    startY: 54,
    margin: { left: MARGIN, right: MARGIN, top: 30, bottom: 30 },
    styles: { fontSize, cellPadding: CELL_PADDING, overflow: "linebreak", valign: "middle" },
    headStyles: { fillColor: [33, 33, 33], textColor: 255 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles,
    // Nem os mínimos cabem na largura: continua as colunas na página seguinte,
    // repetindo a primeira para identificar a linha.
    ...(fits ? {} : { horizontalPageBreak: true, horizontalPageBreakRepeat: 0 }),
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      const path = images[data.row.index]?.[data.column.index];
      if (!path) return;
      const img = imageData[resolveImageUrl(path)];
      if (!img) return;
      const size = Math.min(IMAGE_SIZE, data.cell.width - 2, data.cell.height - 2);
      const x = data.cell.x + (data.cell.width - size) / 2;
      const y = data.cell.y + (data.cell.height - size) / 2;
      // alias = url: o jsPDF reaproveita a imagem já embutida em vez de
      // decodificar o base64 de novo a cada célula.
      doc.addImage(img, "JPEG", x, y, size, size, resolveImageUrl(path), "FAST");
    },
    didDrawPage: () => {
      const pageSize = doc.internal.pageSize;
      doc.setFontSize(7);
      doc.setTextColor(120);
      doc.text(
        `Página ${doc.internal.getNumberOfPages()}`,
        pageSize.getWidth() - 60,
        pageSize.getHeight() - 12,
      );
    },
  });

  return doc;
};

// Reduz a foto para uma miniatura JPEG quadrada (recorte central).
const drawThumbnail = (source, width, height) => {
  const side = Math.min(width, height);
  if (!side) return null;
  const canvas = document.createElement("canvas");
  canvas.width = IMAGE_PX;
  canvas.height = IMAGE_PX;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff"; // PNG transparente vira fundo branco no JPEG
  ctx.fillRect(0, 0, IMAGE_PX, IMAGE_PX);
  ctx.drawImage(source, (width - side) / 2, (height - side) / 2, side, side, 0, 0, IMAGE_PX, IMAGE_PX);
  return canvas.toDataURL("image/jpeg", 0.8);
};

const decodeWithImage = (blob) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const el = new Image();
    el.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(el);
    };
    el.onerror = (e) => {
      URL.revokeObjectURL(objectUrl);
      reject(e);
    };
    el.src = objectUrl;
  });

// Baixa pelo CDN e gera a miniatura. createImageBitmap decodifica fora da
// thread principal (a tela não trava); sem suporte cai no <img>.
// Falha (CORS, 404, timeout) → null: a célula fica em branco, o PDF sai igual.
const loadThumbnail = async (url) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), IMAGE_TIMEOUT_MS);
  try {
    const res = await fetch(toCdnUrl(url), { mode: "cors", signal: controller.signal });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (typeof createImageBitmap === "function") {
      try {
        const bitmap = await createImageBitmap(blob);
        try {
          return drawThumbnail(bitmap, bitmap.width, bitmap.height);
        } finally {
          bitmap.close?.();
        }
      } catch (e) {
        // formato que o createImageBitmap não aceita: tenta pelo <img>
      }
    }
    const img = await decodeWithImage(blob);
    return drawThumbnail(img, img.naturalWidth, img.naturalHeight);
  } catch (e) {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

const loadImages = async (urls) => {
  const result = {};
  const queue = [];
  urls.forEach((url) => {
    if (thumbCache.has(url)) {
      result[url] = thumbCache.get(url);
    } else {
      queue.push(url);
    }
  });
  if (!queue.length) return result;

  const deadline = Date.now() + IMAGE_BUDGET_MS;
  const worker = async () => {
    while (queue.length && Date.now() < deadline) {
      const url = queue.shift();
      const data = await loadThumbnail(url);
      if (data) {
        thumbCache.set(url, data); // falha não entra: tenta de novo no próximo PDF
        result[url] = data;
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(IMAGE_CONCURRENCY, queue.length) }, worker),
  );
  return result;
};

export const exportGridToPdf = async ({
  fileName = "dados_exportados.pdf",
  resolveImageUrl = (p) => p,
  ...options
}) => {
  const { images } = buildPdfTable(options.columns, options.rows);
  const urls = new Set();
  images.forEach((line) => line.forEach((p) => p && urls.add(resolveImageUrl(p))));
  const imageData = urls.size ? await loadImages(urls) : {};

  const doc = buildGridPdf({ ...options, imageData, resolveImageUrl });
  if (!doc) return false;
  doc.save(fileName);
  return true;
};
